import { LLMCoreService } from '../core/llm.core.service';
import { apiClient } from '../../apiClient';

/**
 * Medlink Domain LLM Service
 * Extends Core Service with Medlink-specific capabilities (Claims, EOBs, etc.)
 */
export class MedlinkLLMService extends LLMCoreService {
    constructor() {
        super('/llm');
    }

    /**
     * Extract claim data from an upload image
     * @param {File} claimImage 
     * @param {string} [templateId]
     * @returns {Promise<Object>}
     */
    async extractClaimFromImage(claimImage, templateId) {
        const normalizeMimeType = (file) => {
            if (!file) return '';
            const currentType = String(file.type || '').toLowerCase();
            if (currentType && currentType !== 'application/octet-stream') return currentType;
            const filename = String(file.name || '').toLowerCase();
            if (filename.endsWith('.heic')) return 'image/heic';
            if (filename.endsWith('.heif')) return 'image/heif';
            if (filename.endsWith('.jpg') || filename.endsWith('.jpeg')) return 'image/jpeg';
            if (filename.endsWith('.png')) return 'image/png';
            if (filename.endsWith('.webp')) return 'image/webp';
            if (filename.endsWith('.gif')) return 'image/gif';
            if (filename.endsWith('.tif') || filename.endsWith('.tiff')) return 'image/tiff';
            return currentType || 'application/octet-stream';
        };

        try {
            const formData = new FormData();
            let uploadFile = claimImage;
            const normalizedType = normalizeMimeType(claimImage);

            if (claimImage && normalizedType && normalizedType !== claimImage.type && typeof File !== 'undefined') {
                uploadFile = new File([claimImage], claimImage.name || 'upload-image', {
                    type: normalizedType,
                    lastModified: claimImage.lastModified || Date.now(),
                });
            }

            formData.append('claimImage', uploadFile, uploadFile?.name || claimImage?.name || 'upload-image');
            if (templateId) {
                formData.append('templateId', templateId);
            }
            const response = await apiClient.post(`${this.BASE_URL}/claim/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response;
        } catch (error) {
            let errorMsg = 'Image upload failed.';
            if (error?.response?.data?.error) {
                errorMsg = error.response.data.error;
            } else if (error?.message) {
                errorMsg = error.message;
            }
            return { success: false, error: errorMsg };
        }
    }

    /**
     * Analyze EOB document with BULK context
     * @param {File} file 
     * @param {Object} bulkContext 
     * @returns {Promise<Object>}
     */
    async analyzeBulkEob(file, bulkContext, confirmedIdentifiers = null) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('orgId', bulkContext.orgId || localStorage.getItem('orgId'));
            formData.append('bulkContext', JSON.stringify({
                patientAssignments: bulkContext.patientAssignments.map(pa => ({
                    patientId: pa.patientId,
                    patientName: pa.patientName,
                    claims: pa.claims.map(claim => ({
                        claimId: claim.id || claim.claimId,
                        caseNumber: claim.caseNumber,
                        dateOfService: claim.dateOfService,
                        medications: (claim.medicationBreakdown || claim.medications || []).map(med => ({
                            medicationId: med.medicationId || med.id,
                            medicationName: med.medicationName || med.name,
                            ndcCode: med.ndcCode,
                            billedAmount: med.billedAmount || 0,
                            fullRxNumber: med.fullRxNumber,
                            genericName: med.genericName,
                            brandName: med.brandName
                        }))
                    }))
                }))
            }));
            if (confirmedIdentifiers) {
                formData.append('confirmedIdentifiers', JSON.stringify(confirmedIdentifiers));
            }

            const response = await apiClient.post('/llm/eob/analyze-bulk', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return response;
        } catch (error) {
            console.error('Error analyzing bulk EOB:', error);
            throw new Error(error.response?.data?.error || error.response?.data?.details || 'Failed to analyze EOB document');
        }
    }

    /**
     * Split bulk EOB PDF into individual PDFs per claim using AI
     * @param {File} file - The EOB PDF file to split
     * @returns {Promise<Object>} Result with processedClaims array and unaccountedPages
     */
    async splitAndProcessBulkEob(file, confirmedIdentifiers = null) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (confirmedIdentifiers) {
                formData.append('confirmedIdentifiers', JSON.stringify(confirmedIdentifiers));
            }

            const response = await apiClient.post('/llm/eob/split-and-process', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return response;
        } catch (error) {
            console.error('Error splitting bulk EOB:', error);
            throw new Error(error.response?.data?.error || error.response?.data?.details || 'Failed to split EOB document');
        }
    }

    /**
     * Extract non-financial metadata from EOB (contacts, references, denial metadata)
     * @param {File} file
     * @returns {Promise<Object>}
     */
    async extractEobMetadata(file, confirmedIdentifiers = null) {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (confirmedIdentifiers) {
                formData.append('confirmedIdentifiers', JSON.stringify(confirmedIdentifiers));
            }

            const response = await apiClient.post('/llm/eob/extract-metadata', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return response;
        } catch (error) {
            console.error('Error extracting EOB metadata:', error);
            throw new Error(error.response?.data?.error || error.response?.data?.details || 'Failed to extract EOB metadata');
        }
    }

    /**
     * Preflight identifier extraction: patient, claim ids, rx numbers
     * @param {File} file
     * @returns {Promise<Object>}
     */
    async preflightIdentifiers(file) {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await apiClient.post('/llm/eob/preflight-identifiers', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return response;
        } catch (error) {
            console.error('Error running EOB preflight identifiers:', error);
            throw new Error(error.response?.data?.error || error.response?.data?.details || 'Failed to extract preflight identifiers');
        }
    }

    /**
     * Extract claim data from email
     * @param {string} emailBody 
     * @param {string} [messageId] 
     * @param {Array} [attachments] 
     * @returns {Promise<Object>}
     */
    async extractFromEmail(emailBody, messageId, attachments) {
        try {
            const payload = { emailBody };
            if (messageId) payload.messageId = messageId;
            if (attachments && attachments.length > 0) payload.attachments = attachments;

            const response = await apiClient.post(`${this.BASE_URL}/claim/extract-from-email`, payload);
            return response;
        } catch (error) {
            console.error('Error extracting claim from email:', error);
            throw new Error(error.response?.data?.error || 'Failed to extract claim from email');
        }
    }

    /**
     * Transcribe audio file to text
     * @param {File|Blob} audioFile 
     * @returns {Promise<Object>}
     */
    async transcribeAudio(audioFile) {
        try {
            const formData = new FormData();
            formData.append('audio', audioFile);

            const response = await apiClient.post(`${this.BASE_URL}/audio/transcribe`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response;
        } catch (error) {
            console.error('Error transcribing audio:', error);
            throw error;
        }
    }

    /**
     * Extract activity data from audio direct (one-step)
     * @param {File|Blob} audioFile 
     * @returns {Promise<Object>}
     */
    async extractActivityFromAudio(audioFile) {
        try {
            const formData = new FormData();
            formData.append('audio', audioFile);

            const response = await apiClient.post(`${this.BASE_URL}/activity/extract`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response;
        } catch (error) {
            console.error('Error extracting activity from audio:', error);
            throw error;
        }
    }

    /**
     * Extract activity data from text
     * @param {string} text 
     * @returns {Promise<Object>}
     */
    async extractActivityFromText(text) {
        try {
            const response = await apiClient.post(`${this.BASE_URL}/activity/extract-text`, { text });
            return response;
        } catch (error) {
            console.error('Error extracting activity from text:', error);
            throw error;
        }
    }
}

export const medlinkLLMService = new MedlinkLLMService();
